import { useLazyQuery, useMutation } from "@apollo/client";
import { Grid, Typography, useMediaQuery } from "@material-ui/core";
import Dialog from "components/Dialog";
import SearchBox from "components/SearchBox";
import Table from "components/Table";
import { ROW_PER_PAGE_OPTIONS } from "config";
import { theme } from "config/theme";
import HTTP_STATUS_CODES from "constants/http-status-code";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { displayAreas } from "domains/Tasks/data";
import { ClientRefsAddress } from "generated/graphql";
import { DELETE_CLIENT_REF_ADDRESS } from "graphql/Address/mutations";
import { ALL_ADDRESS_BY_USER_ID } from "graphql/Address/queries";
import { useCurrentUser } from "hooks";
import React from "react";
import { Action } from "ui/ActionGroup/actiongroup";
import { useClientRefAddressColumns } from "../data";
import { useStyles } from "../styles";

interface AddressTableProps {
  onRowMenuClick?(key: string, row: ClientRefsAddress): void;
  multipleActions?: Action[];
  addressToDelete?: ClientRefsAddress;
}

export const AddressTable = ({ onRowMenuClick, multipleActions, addressToDelete }: AddressTableProps) => {
  const style = useStyles();
  const { text } = useText();
  const { user } = useCurrentUser();
  const [searchTerm, setSearchTerm] = React.useState("");
  const columns = useClientRefAddressColumns(text);
  const [clientRefAddress, setClientRefAddress] = React.useState<ClientRefsAddress[]>([]);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [deleteClientRefAddress] = useMutation(DELETE_CLIENT_REF_ADDRESS);
  const dispatchAlert = useAlertDispatch();
  const [filterRows] = React.useState([]);
  const [filterOptions] = React.useState([]);
  const handleChangePage = (event: unknown, newpage: number) => {
    setPage(newpage);
  };
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const handleSearchTerm = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value.toLowerCase()),
    []
  );
 React.useEffect(() => {
   if (addressToDelete?._id) {
     setOpenModal(true);
   }
 }, [addressToDelete]);
    
    const handleDeletion = async () => {
        await deleteClientRefAddress({
          variables: { input: { id: addressToDelete?._id } },
          refetchQueries: [
            {
              query: ALL_ADDRESS_BY_USER_ID,
              variables: {
                params: { clientID: user?.company_user_clientId },
              },
            },
          ],
        })
          .then((response) => {
            if (response.data.deleteClientRefAddress.code === HTTP_STATUS_CODES.OK) {
              dispatchAlert({
                message: response.data.deleteClientRefAddress.message,
                severity: "success",
              });
              setOpenModal(false);
            } 
          })
          .catch((error) => {
            dispatchAlert({
              message: error.message,
              severity: "error",
            });
          });;
    };
    

    const handleCloseDeletion = () => {
        setOpenModal(false);
      
    };
  const [allClientRefAddressByClientId, { loading, data }] = useLazyQuery(ALL_ADDRESS_BY_USER_ID);
  React.useEffect(() => {
    allClientRefAddressByClientId({ variables: { params: { clientID: user.company_user_clientId } } });
  }, []);
  React.useEffect(() => {
    const result = data?.results;
    if (result?.code === HTTP_STATUS_CODES.OK) {
      setClientRefAddress(result?.data);
    }
  }, [data]);

  return (
    <Grid container spacing={1} className={style.body}>
      <Grid container direction="row">
        <Grid item xs={12} className={style.headerContainer}>
          {!isSmallScreen && (
            <div>
              <Typography className={style.bodyTitle}>{text.all.my_address}</Typography>
            </div>
          )}
          <div className={style.searchTextAddress}>
            <SearchBox value={searchTerm} onChange={handleSearchTerm} />
          </div>
        </Grid>
      </Grid>
      <Grid className={style.table}>
        <Table
          searchTerm={searchTerm}
          tableHeight={isSmallScreen ? "49vh" : "53vh"}
          columns={columns}
          rowsIdentifier="_id"
          data={filterRows.length > 0 || filterOptions.length > 0 ? filterRows : clientRefAddress}
          isLoading={loading}
          showMenu
          multipleActions={multipleActions}
          onRowMenuClick={onRowMenuClick}
          onChangePage={handleChangePage}
          setRowsPerPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          currentPage={page}
          rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
          countTotal={clientRefAddress?.length}
        />
      </Grid>
      <Dialog
        title="Address Deletion"
        open={openModal}
        confirmButtonTitle={text.all.validate}
        handleClose={handleCloseDeletion}
        handleConfirm={handleDeletion}
        cancelButtonTitle={text.all.cancel}
        cancelButtonStyle={style.cancelButtonModal}
        footerStyle={style.footerStyleModal}
      >
        <Typography className={style.textDeleteModal}>{text.all.want_to_delete}</Typography>
      </Dialog>
    </Grid>
  );
};
